/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { inputBaseClasses } from "@mui/material/InputBase";
import { createTheme, SimplePaletteColorOptions } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import variablesScss from "./../Themes/variables.module.scss";

export default class Themes {
    public static createTheme() {
        return createTheme({
            palette: {
                primary: Themes.getPalettePrimary(),
                secondary: Themes.getPaletteSecondary(),
                error: Themes.getPaletteError(),
                warning: Themes.getPaletteWarning(),
                info: Themes.getPaletteInfo(),
                success: Themes.getPaletteSuccess(),
            },
            shape: {
                borderRadius: 0,
            },
            breakpoints: {
                unit: Themes.getBreakpointUnit(),
                values: Themes.getBreakpointValues(),
            },
            components: {
                MuiTextField: {
                    defaultProps: {
                        margin: "dense",
                        variant: "outlined",
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            [`&.${inputBaseClasses.disabled}`]: {
                                backgroundColor: variablesScss.colorsInputBackgroundReadonly,
                            }
                        },
                    },
                    defaultProps: {
                        size: "small",
                        fullWidth: true,
                        autoComplete: "off",
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        root: {
                            color: variablesScss.colorsFeldueberschrift,
                        }
                    },
                    defaultProps: {
                        shrink: true,
                    },
                }
            }
        });
    }

    private static getPalettePrimary(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsPrimaryLight,
            main: variablesScss.colorsPrimaryMain,
            dark: variablesScss.colorsPrimaryDark,
        };
    }

    private static getPaletteSecondary(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsSecondaryLight,
            main: variablesScss.colorsSecondaryMain,
            dark: variablesScss.colorsSecondaryDark,
        };
    }

    private static getPaletteError(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsErrorLight,
            main: variablesScss.colorsErrorMain,
            dark: variablesScss.colorsErrorDark,
        };
    }

    private static getPaletteWarning(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsWarningLight,
            main: variablesScss.colorsWarningMain,
            dark: variablesScss.colorsWarningDark,
        };
    }

    private static getPaletteInfo(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsInfoLight,
            main: variablesScss.colorsInfoMain,
            dark: variablesScss.colorsInfoDark,
        };
    }

    private static getPaletteSuccess(): SimplePaletteColorOptions {
        return {
            light: variablesScss.colorsSuccessLight,
            main: variablesScss.colorsSuccessMain,
            dark: variablesScss.colorsSuccessDark,
        };
    }

    private static getBreakpointUnit(): string {
        return variablesScss.breakpointsLg.slice(-2);
    }

    private static getBreakpointValues() {
        return {
            xs: 0,
            sm: Themes.convertBreakpointToNumber(variablesScss.breakpointsSm),
            md: Themes.convertBreakpointToNumber(variablesScss.breakpointsSm),
            lg: Themes.convertBreakpointToNumber(variablesScss.breakpointsLg),
            xl: Themes.convertBreakpointToNumber(variablesScss.breakpointsXl),
        };
    }

    private static convertBreakpointToNumber(breakpoint: string): number {
        return parseInt(breakpoint.slice(0, -2));
    }
}
